import React, { useState } from "react";
import { IndividualizedPlanFlow } from "./document-flow/IndividualizedPlanFlow";
import { CorrespondenceFlow } from "./document-flow/CorrespondenceFlow";
import { ReportsFlow } from "./document-flow/ReportsFlow";
import { LessonCreatorFlow, LessonResource } from "./document-flow/LessonCreatorFlow";
import { SubscriptionBanner } from "./subscription/SubscriptionBanner";
import { useSubscriptionGuard } from "../hooks/useSubscriptionGuard";
import { useLocation } from "react-router-dom";

// Define types for our dashboard sections
interface DashboardSection {
  id: string;
  title: string;
  description: string;
  themeClass: string;
  icon: JSX.Element;
  badge?: string;
}

interface GymeaEduDashboardProps {
  publicMode?: boolean;
  onSectionClick?: (sectionId: string) => void;
}

const dashboardHashSectionIds = [
  "lesson-creator",
  "individualized-plan",
  "reports",
  "correspondence",
];

const getHashSectionId = (locationHash: string) => {
  const hash = locationHash.slice(1);
  return dashboardHashSectionIds.includes(hash) ? hash : null;
};

const scrollToFlowHeader = (attempt = 0) => {
  const maxAttempts = 5;
  const flowHeader = document.querySelector(".flow-header");

  if (flowHeader) {
    requestAnimationFrame(() => {
      const headerRect = flowHeader.getBoundingClientRect();
      const navBarHeight = window.innerWidth <= 768 ? 70 : 80;
      const additionalPadding = 20;
      const totalOffset = navBarHeight + additionalPadding;

      window.scrollTo({
        top: window.scrollY + headerRect.top - totalOffset,
        behavior: "smooth",
      });
    });
  } else if (attempt < maxAttempts) {
    setTimeout(() => scrollToFlowHeader(attempt + 1), 150);
  } else {
    const element = document.getElementById("expanded-content");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
};

export const GymeaEduDashboard: React.FC<GymeaEduDashboardProps> = ({
  publicMode = false,
  onSectionClick,
}) => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [mountKey, setMountKey] = useState<number>(0);

  // Lesson creator hero state
  const [lessonTopic, setLessonTopic] = useState('');
  const [selectedResources, setSelectedResources] = useState<LessonResource[]>(['teaching_program']);

  const { hasAccess, isLoading } = useSubscriptionGuard();
  const dashboardHasAccess = publicMode || hasAccess;
  const dashboardIsLoading = publicMode ? false : isLoading;
  const dashboardHeaderRef = React.useRef<HTMLHeadingElement>(null);
  const dashboardCardsRef = React.useRef<HTMLDivElement>(null);

  const openSection = React.useCallback((sectionId: string) => {
    setActiveSection(sectionId);
    setMountKey((prev) => prev + 1);
    setTimeout(() => scrollToFlowHeader(), 200);
  }, []);

  React.useEffect(() => {
    const hashSectionId = getHashSectionId(location.hash);
    if (hashSectionId) {
      openSection(hashSectionId);
    }
  }, [location.hash, openSection]);

  React.useEffect(() => {
    // Capture refs at the beginning of the effect
    const dashboardRef = dashboardCardsRef.current;

    // Trigger underline animation when dashboard loads
    const timer = setTimeout(() => {
      if (dashboardHeaderRef.current) {
        dashboardHeaderRef.current.classList.add("animate-underline");
      }
    }, 500);

    // Create intersection observer for card animations
    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            // Stagger the animations
            setTimeout(() => {
              entry.target.classList.add("animate-in");
            }, index * 50);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "50px 0px -50px 0px",
      }
    );

    // Observe dashboard cards — track timeouts so cleanup can cancel them
    const observeTimers: ReturnType<typeof setTimeout>[] = [];
    if (dashboardRef) {
      const cards = dashboardRef.querySelectorAll(".dashboard-card-wrapper");
      cards.forEach((card, index) => {
        const t = setTimeout(() => {
          cardObserver.observe(card);
        }, index * 10);
        observeTimers.push(t);
      });
    }

    // Fallback: trigger animations after delay if intersection observer fails
    const fallbackTimer = setTimeout(() => {
      if (dashboardRef) {
        const cards = dashboardRef.querySelectorAll(".dashboard-card-wrapper");
        cards.forEach((card, index) => {
          if (!card.classList.contains("animate-in")) {
            setTimeout(() => {
              card.classList.add("animate-in");
            }, index * 100);
          }
        });
      }
    }, 2000);

    return () => {
      clearTimeout(timer);
      clearTimeout(fallbackTimer);
      observeTimers.forEach(clearTimeout);
      cardObserver.disconnect();
    };
  }, []);

  // Secondary tiles — plans, reports, correspondence
  const dashboardSections: DashboardSection[] = [
    {
      id: "individualized-plan",
      title: "Personalised plan",
      description:
        "Give personalised support with evidence-based strategies and curriculum-aligned SMART goals.",
      themeClass: "card-theme-teal",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
    },
    {
      id: "reports",
      title: "Report comments",
      description: "Turn quick student notes into polished report comments.",
      themeClass: "card-theme-purple",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
    },
    {
      id: "correspondence",
      title: "Correspondence",
      description:
        "Professional emails and newsletters that actually get read.",
      themeClass: "card-theme-amber",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
          <polyline points="22,6 12,13 2,6"></polyline>
        </svg>
      ),
    },
  ];

  const handleSectionClick = React.useCallback(
    (sectionId: string) => {
      if (onSectionClick) {
        onSectionClick(sectionId);
        return;
      }
      if (!dashboardHasAccess && !dashboardIsLoading) return;
      openSection(sectionId);
    },
    [dashboardHasAccess, dashboardIsLoading, onSectionClick, openSection]
  );

  const toggleResource = (resource: LessonResource) => {
    setSelectedResources((prev) =>
      prev.includes(resource) ? prev.filter((r) => r !== resource) : [...prev, resource]
    );
  };

  const handleStartLessonCreator = () => {
    if (!dashboardHasAccess && !dashboardIsLoading) return;
    openSection("lesson-creator");
  };

  const handleBackClick = () => {
    setActiveSection(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderExpandedContent = () => {
    switch (activeSection) {
      case "lesson-creator":
        return (
          <div className="document-flow-container" key={`lesson-creator-${mountKey}`}>
            <div className="flow-header">
              <h2>Create a lesson</h2>
              <button className="back-button" onClick={handleBackClick}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Back
              </button>
            </div>
            <LessonCreatorFlow
              initialTopic={lessonTopic}
              initialResources={selectedResources.length > 0 ? selectedResources : ['teaching_program']}
              onBackToDashboard={handleBackClick}
            />
          </div>
        );
      case "individualized-plan":
        return (
          <IndividualizedPlanFlow
            key={`individualized-plan-${mountKey}`}
            onBackToDashboard={handleBackClick}
          />
        );
      case "correspondence":
        return (
          <CorrespondenceFlow
            key={`correspondence-${mountKey}`}
            onBackToDashboard={handleBackClick}
          />
        );
      case "reports":
        return (
          <ReportsFlow
            key={`reports-${mountKey}`}
            onBackToDashboard={handleBackClick}
          />
        );
      default:
        return null;
    }
  };

  const resourceOptions: { id: LessonResource; label: string }[] = [
    { id: 'teaching_program', label: 'Teaching program' },
    { id: 'slides', label: 'Slideshow' },
    { id: 'worksheet', label: 'Worksheet' },
  ];

  return (
    <div className="page-container dashboard-container">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <h1 ref={dashboardHeaderRef}>Totally tailored lesson planning</h1>
      </div>

      {/* Subscription Banner - Show when user doesn't have access */}
      {!dashboardHasAccess && !dashboardIsLoading && <SubscriptionBanner />}

      {/* Lesson Creator Hero — single branded panel on the page background */}
      <div className="lesson-creator-hero">
            <h2 className="lesson-creator-hero__heading">Let's create</h2>

            <label className="lesson-creator-hero__label" htmlFor="hero-topic">
              What do you want to teach?
            </label>
            <textarea
              id="hero-topic"
              className="lesson-creator-hero__topic"
              rows={3}
              placeholder="For example, arrays for multiplication, writing compound sentences, the water cycle..."
              value={lessonTopic}
              onChange={(e) => setLessonTopic(e.target.value)}
            />

            <label className="lesson-creator-hero__label">What would you like to create?</label>
            <div className="lesson-creator-resource-picker">
              {resourceOptions.map((opt) => {
                const active = selectedResources.includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    className={`lesson-creator-resource-chip ${active ? 'active' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleResource(opt.id)}
                    />
                    <span className="lesson-creator-resource-chip__check">
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </span>
                    {opt.label}
                  </label>
                );
              })}
            </div>

            <div className="lesson-creator-hero__footer">
              <button
                className="lesson-creator-continue-btn"
                onClick={handleStartLessonCreator}
                disabled={(!dashboardHasAccess && !dashboardIsLoading) || selectedResources.length === 0}
              >
                Continue
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            </div>
      </div>

      {/* Secondary tools — grounded in a lighter surface below */}
      <div className="dashboard-cards-section">
        <div className="dashboard-cards-container" ref={dashboardCardsRef}>
          <div className="dashboard-other-tools">
            <p className="dashboard-other-tools__heading">Other tools</p>
            <div className="dashboard-cards">
              {dashboardSections.map((section) => (
                <div key={section.id} className="dashboard-card-wrapper">
                  <div
                    className={`dashboard-card ${section.themeClass} ${
                      !dashboardHasAccess && !dashboardIsLoading
                        ? "dashboard-card--disabled"
                        : ""
                    }`}
                    onClick={() => handleSectionClick(section.id)}
                  >
                    {section.badge && (
                      <div className="dashboard-card-badge">{section.badge}</div>
                    )}
                    <div className="card-icon-container">{section.icon}</div>
                    <h2>{section.title}</h2>
                    <p className="card-description">{section.description}</p>
                    <div className="card-footer">
                      <button
                        className="get-quote-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSectionClick(section.id);
                        }}
                      >
                        Get Started
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content Area */}
      {activeSection && (
        <div id="expanded-content" className="expanded-content">
          {renderExpandedContent()}
        </div>
      )}
    </div>
  );
};
